import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders, faTimes, faUser, faCalendarAlt, faClipboardList, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../loadBalancer';
import api from '../../api';

function DashboardDropdown({ selectedClientId, selectedtoDate, selectedFromDate, selectedScopeId }) {
  const { accessToken, client, scope } = useAuth();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);
  const [selectedClientIdState, setSelectedClientIdState] = useState(null);

  const isRtl = useSelector((state) => state.themeConfig.rtlClass) === 'rtl';

  const [clients, setClients] = useState([]);
  const [scopes, setScopes] = useState([]);

  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingScopes, setLoadingScopes] = useState(false);

  // Fetch Clients from API
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        let baseUrls= getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrls}/client/getAllClient`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (response.data && Array.isArray(response.data)) {
          const clientOptions = response.data
            .filter(client_s => client.includes(client_s._id))
            .map(client => ({ label: client.client, value: client._id }));
          setClients(clientOptions);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [accessToken, client]);

  // Fetch Scopes based on selected Client
  const fetchScopes = async (clientId) => {
    setLoadingScopes(true);
    setScopes([]); // Clear previous scopes
    try {
      let baseUrls= getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrls}/qaDashboard/getClientByScope?clientId=${clientId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.data && Array.isArray(response.data.data)) {
        const scopeOptions = response.data.data
          .filter(scopeItem => scope.includes(scopeItem._id))
          .map(scopeItem => ({ label: scopeItem.scope, value: scopeItem._id }));
        setScopes(scopeOptions);
      }
    } catch (error) {
      console.error('Error fetching scopes:', error);
      setScopes([]);
    } finally {
      setLoadingScopes(false);
    }
  };

  useEffect(() => {
    if (selectedClientIdState) {
      fetchScopes(selectedClientIdState);
    }
  }, [selectedClientIdState]); // Runs when client changes

  // Clear all fields
  const handleClear = () => {
    setSelectedClient(null);
    setSelectedScope(null);
    setSelectedClientIdState(null);
    setFromDate(null);
    setToDate(null);

    // Update parent state
    selectedClientId(null);
    selectedScopeId(null);
    selectedFromDate(null);
    selectedtoDate(null);
  };

  return (
    <div className="relative">
      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute right-10 top-2 bg-gray-800 text-white text-xs py-1 px-3 rounded-md shadow-md">
          Open Filters
        </div>
      )}

      {/* Filter Icon Button with Pulse Effect */}
      <button
        className="absolute right-4 text-blue-600 text-2xl transition-all hover:text-green-600 z-50 animate-pulse"
        onClick={() => setIsFilterOpen(!isFilterOpen)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <FontAwesomeIcon icon={faSliders} />
      </button>

      {/* Filter Popup (Floating Bubble) */}
      {isFilterOpen && (
        <div className="absolute right-0 top-0 ml-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white border border-blue-400 rounded-xl shadow-2xl p-5 w-64 z-50 animate-slide-in backdrop-blur-md bg-opacity-80">
          {/* Close Button */}
          <button
            className="absolute top-2 right-2 text-white text-lg transition-all hover:text-red-300"
            onClick={() => setIsFilterOpen(false)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          {/* Title */}
          <h2 className="text-lg font-semibold text-white text-center mb-4">Filter Options</h2>

          {/* Dropdowns */}
          <form className="space-y-3">
            {/* Client */}
            <div className="flex items-center space-x-2 bg-white bg-opacity-10 rounded-md p-2">
              <FontAwesomeIcon icon={faUser} className="text-white" />
              <Select
                options={clients}
                isLoading={loadingClients}
                placeholder="Select Client..."
                className="w-full text-sm bg-transparent text-black"
                value={selectedClient}
                onChange={(selected) => {
                  setSelectedClient(selected);
                  setSelectedScope(null);
                  selectedScopeId(null);
                  setSelectedClientIdState(selected.value);
                  selectedClientId(selected.value);
                }}
              />
            </div>

            {/* Scope */}
            <div className="flex items-center space-x-2 bg-white bg-opacity-10 rounded-md p-2">
              <FontAwesomeIcon icon={faClipboardList} className="text-white" />
              <Select
                options={scopes}
                isLoading={loadingScopes}
                placeholder="Select Scope..."
                className="w-full text-sm bg-transparent text-black"
                value={selectedScope}
                onChange={(selected) => {
                  setSelectedScope(selected);
                  selectedScopeId(selected.value);
                }}
              />
            </div>

            {/* From Date */}
            <div className="flex items-center space-x-2 bg-white bg-opacity-10 rounded-md p-2">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-white" />
              <DatePicker
                selected={fromDate}
                onChange={(date) => {
                  setFromDate(date);
                  selectedFromDate(date);
                }}
                placeholderText="From Date"
                className="w-full p-2 bg-transparent text-white placeholder-white border-none"
              />
            </div>

            {/* To Date */}
            <div className="flex items-center space-x-2 bg-white bg-opacity-10 rounded-md p-2">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-white" />
              <DatePicker
                selected={toDate}
                onChange={(date) => {
                  setToDate(date);
                  selectedtoDate(date);
                }}
                placeholderText="To Date"
                className="w-full p-2 bg-transparent text-white placeholder-white border-none"
              />
            </div>

            {/* Clear Button */}
            <button
              type="button"
              className="w-full bg-red-400 hover:bg-red-700 text-white font-bold py-2 px-4 rounded mt-4 transition-all"
              onClick={handleClear}
            >
              <FontAwesomeIcon icon={faTrash} className="mr-2" />
              Clear
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default DashboardDropdown;
